/* eslint-disable react-hooks/exhaustive-deps */
import clsx from 'clsx';
import { useState, useMemo, useEffect } from 'react';
import Icon from '../../../ui/Icon';
import DayItem from '../../Calendar/DayItem';
import CalendarPopup from '../../Calendar';
import { useSchedule } from '../../../../context/ScheduleContext';
import { client as api } from '../../../../services/api';
import {
  ArProps,
  DayOfWeekType,
  ScheduleType as ScheduleTypeEnum,
  Period,
} from '../../../../types/types';
import { SCHEDULE_OPTIONS, AVAILABLE_HOURS, PERIOD_ICONS } from '../../../../types/constants';
import Loading from '../../../Loading';

interface VideoconferenceProps {
  orderId: string;
  setSelectAc: React.Dispatch<React.SetStateAction<boolean>>;
}

const Videoconference = ({ orderId, setSelectAc }: VideoconferenceProps) => {
  const [periodSelected, setPeriodSelected] = useState<Period | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [typeSchedule, setTypeSchedule] = useState<ScheduleTypeEnum>(ScheduleTypeEnum.DateHour);
  const [hourSelected, setHourSelected] = useState<string>('');
  const [customDate, setCustomDate] = useState<Date | null>(null);
  const [displayedDate, setDisplayedDate] = useState(new Date());
  const [selected, setSelected] = useState<Boolean>(false);
  const [postoSearch, setPostoSearch] = useState<string>('');
  const [posto, setPosto] = useState<ArProps[]>([]);
  const [scheduleData, setScheduleData] = useState<any[]>([]);
  const [showDateTimeSelector, setShowDateTimeSelector] = useState(false);
  const [error, setError] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [loadingPostos, setLoadingPostos] = useState(false);
  const [loadingAgenda, setLoadingAgenda] = useState(false);
  const { setData } = useSchedule();

  const today = new Date();
  const actualYear = displayedDate.getFullYear();
  const actualMonth = displayedDate.getMonth();
  const actualMonthName = displayedDate
    .toLocaleDateString('pt-BR', { month: 'long' })
    .replace(/^\w/, (l) => l.toUpperCase());

  const daysOfWeek = useMemo<DayOfWeekType[]>(() => {
    const todayMidnight = new Date(today);
    todayMidnight.setHours(0, 0, 0, 0);

    // Se for busca por local, a disponibilidade vem da API
    if (typeSchedule === 'location') {
      const availableDates = new Set(
        scheduleData.filter((d) => d.horariosDiponiveis.length > 0).map((d) => d.data)
      );
      const daysInMonth = new Date(actualYear, actualMonth + 1, 0).getDate();
      return Array.from({ length: daysInMonth }, (_, i) => {
        const date = new Date(actualYear, actualMonth, i + 1);
        const dateString = date.toISOString().split('T')[0];
        return {
          diaSemana: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'][date.getDay()],
          data: date.getDate().toString(),
          mes: date.toLocaleDateString('pt-BR', { month: 'short' }),
          disabled: !availableDates.has(dateString),
        };
      });
    }

    // Lógica original para agendamento por data/hora
    const daysInMonth = new Date(actualYear, actualMonth + 1, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => {
      const date = new Date(actualYear, actualMonth, i + 1);
      const isPast = date.getTime() < todayMidnight.getTime();
      const isSunday = date.getDay() === 0;

      return {
        diaSemana: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'][date.getDay()],
        data: date.getDate().toString(),
        mes: date.toLocaleDateString('pt-BR', { month: 'short' }),
        disabled: isPast || isSunday,
      };
    });
  }, [actualYear, actualMonth, today, typeSchedule, scheduleData]);

  const firstAvailableIndex = daysOfWeek.findIndex((d) => !d.disabled);
  const [visibleStartIndex, setVisibleStartIndex] = useState<number>(
    firstAvailableIndex >= 0 ? firstAvailableIndex : 0
  );

  useEffect(() => {
    const firstIdx = daysOfWeek.findIndex((d) => !d.disabled);
    setVisibleStartIndex(firstIdx >= 0 ? firstIdx : 0);
    setSelectedDay(null);
  }, [actualMonth, actualYear]);

  useEffect(() => {
    if (customDate) {
      const dayIndex = customDate.getDate() - 1;
      setSelectedDay(dayIndex);
      setVisibleStartIndex(Math.max(0, Math.min(dayIndex - 3, daysOfWeek.length - 7)));
    }
  }, [customDate]);
  useEffect(() => {
    const getFormattedDate = () => {
      if (customDate) {
        return customDate.toISOString().split('T')[0];
      }
      if (selectedDay !== null) {
        const date = new Date(actualYear, actualMonth, selectedDay + 1);
        return date.toISOString().split('T')[0];
      }
      return '';
    };

    setData((prevState) => ({
      ...prevState,
      data: getFormattedDate(),
      hora: hourSelected,
    }));
  }, [selectedDay, customDate, hourSelected, setData, actualYear, actualMonth]);
  const periodos = Object.values(Period);
  const visibleDays = daysOfWeek.slice(visibleStartIndex, visibleStartIndex + 7);

  const availableHours = useMemo(() => {
    let hours: string[] = [];

    if (typeSchedule === 'location') {
      const dateStr = customDate
        ? customDate.toISOString().split('T')[0]
        : selectedDay !== null
          ? new Date(actualYear, actualMonth, selectedDay + 1).toISOString().split('T')[0]
          : '';
      if (dateStr) {
        hours = scheduleData.find((d) => d.data === dateStr)?.horariosDiponiveis || [];
      }
    } else {
      // typeSchedule === 'dateHour'
      if (periodSelected) {
        hours = AVAILABLE_HOURS[periodSelected] || [];
      }
    }

    if (!periodSelected) {
      return typeSchedule === 'location' ? [] : hours;
    }

    // Filtra os horários com base no período selecionado
    return hours.filter((hour) => {
      const [h] = hour.split(':').map(Number);
      if (periodSelected === Period.Morning) return h < 12;
      if (periodSelected === Period.Afternoon) return h >= 12 && h < 18;
      if (periodSelected === Period.Evening) return h >= 18;
      return false;
    });
  }, [
    selectedDay,
    customDate,
    scheduleData,
    actualYear,
    actualMonth,
    typeSchedule,
    periodSelected,
  ]);

  const shouldShowHours = (selectedDay !== null || customDate) && periodSelected;
  const handleScheduleTypeChange = (type: ScheduleTypeEnum) => () => {
    if (type === 'location') {
      setSelectAc(true);
    } else {
      setSelectAc(false);
    }

    // Resetar estados ao trocar de tipo
    setSelectedDay(null);
    setHourSelected('');
    setPeriodSelected(null);
    setCustomDate(null);
    setShowDateTimeSelector(false);
    setScheduleData([]);
    setTypeSchedule(type);
  };
  const handlePrev = () => {
    setVisibleStartIndex((prev) => Math.max(0, prev - 7));
  };

  const handleNext = () => {
    const maxIndex = Math.max(0, daysOfWeek.length - 7);
    setVisibleStartIndex((prev) => Math.min(prev + 7, maxIndex));
  };

  const handlePeriodSelect = (period: Period) => {
    setPeriodSelected(period);
  };

  const handleSearchPosto = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoadingPostos(true);
    try {
      const response = await api.get(`/agendamento/${orderId}/postos/${postoSearch}/true`);

      if (response.data.length === 0) {
        setError('Nenhum posto encontrado');
      }

      setPosto(response.data);
      setSelected(false);
    } catch (error) {
      console.error(error);
      setError('Não foi possível buscar postos. Tente novamente.');
    } finally {
      setLoadingPostos(false);
    }
  };

  const handleSelectedPosto = async (local: number) => {
    const selected = posto.find((p) => p.codigo === local);

    if (!selected || loadingAgenda) return;

    setLoadingAgenda(true);
    setError('');
    try {
      const response = await api.get(`/agendamento/${orderId}/agenda/${local}`);
      if (response?.data?.datas) {
        setScheduleData(response.data.datas);
        setSelected(true);
        setShowDateTimeSelector(true);
        setData((prevState) => {
          return {
            ...prevState,
            idGar: orderId,
            pedido: orderId,
            posto: local,
            detalhePosto: {
              ...prevState.detalhePosto,
              codigo: selected.codigo,
              nomeFantasia: selected.nomeFantasia,
              telefone: selected.telefone,
              endereco: selected.endereco,
              numero: selected.numero,
              complemento: selected.complemento || '',
              cidade: selected.cidade,
              uf: selected.uf,
              cep: selected.cep,
              bairro: selected.bairro,
            },
          };
        });
      } else {
        console.error('Dados de agenda não encontrados na resposta');
        setScheduleData([]);
        setError('Agenda indisponível para este posto.');
      }
    } catch (error) {
      console.error('Erro ao carregar os dados da agenda', error);
      setScheduleData([]);
      setError('Não foi possível carregar a agenda deste posto. Tente novamente.');
    } finally {
      setLoadingAgenda(false);
    }
  };

  return (
    <div className="flex flex-col mx-auto max-w-[712px] w-full min-h-[500px] bg-white md:py-8 px-4 md:px-12 rounded-lg">
      <h2 className="title">Agende a validação</h2>

      <div className="flex items-center gap-6 mb-11">
        {SCHEDULE_OPTIONS.map((option) => (
          <span
            className={clsx(
              'relative flex items-center text-sm text-gray-900 gap-1 cursor-pointer',
              'before:w-[20px] before:h-[20px] before:border-2 before:border-gray-800 before:rounded-full',
              `${
                typeSchedule === option.value
                  ? 'selected before:border-ins-blue after:absolute after:top-[4px] after:left-[4px] after:w-[12px] after:h-[12px] after:bg-ins-blue after:rounded-full'
                  : ''
              }`
            )}
            key={option.value}
            onClick={handleScheduleTypeChange(option.value)}
          >
            {option.label}
          </span>
        ))}
      </div>

      <div
        className={clsx(
          `${typeSchedule === 'location' ? 'flex' : 'hidden'}`,
          'flex-col w-full h-full gap-10 mb-10'
        )}
      >
        <form className="flex flex-col w-full max-w-60" onSubmit={handleSearchPosto}>
          <div className="relative flex w-full">
            <input
              id="search-posto"
              name="search-posto"
              type="text"
              className="w-full h-10 py-2.5 px-4 rounded-lg border border-gray-400 focus:border-black outline-none disabled:opacity-60"
              value={postoSearch}
              disabled={loadingPostos}
              onChange={(e) => setPostoSearch(e.target.value.replace(/\D/g, ''))}
            />
            <button
              type="submit"
              className="absolute top-0 right-0 flex h-full w-10 items-center justify-center disabled:opacity-50"
              disabled={loadingPostos || !postoSearch.trim()}
              aria-label="Buscar posto"
            >
              <Icon name="Search" width={24} height={24} fill="var(--ins-blue)" />
            </button>
          </div>
        </form>
        {(loadingPostos || loadingAgenda) && <Loading loading />}
        <ul
          className={clsx(
            'flex flex-col w-full max-h-40 overflow-y-auto gap-2',
            loadingAgenda && 'pointer-events-none opacity-60'
          )}
        >
          {error && <li className="text-feedback-negative">{error}</li>}
          {posto.map((posto) => (
            <li
              key={posto.codigo}
              onClick={() => handleSelectedPosto(posto.codigo)}
              className={clsx(
                'flex items-center h-[60px] px-6 border border-ins-blue rounded-lg cursor-pointer text-xs transition-all duration-300',
                `${selected ? 'bg-ins-ice-10' : ''}`,
                'hover:bg-ins-ice-10'
              )}
            >
              {posto.nomeFantasia}
            </li>
          ))}
        </ul>
      </div>

      <div
        className={clsx(
          `${typeSchedule === 'dateHour' || (typeSchedule === 'location' && showDateTimeSelector) ? 'flex' : 'hidden'}`,
          'w-full h-full flex flex-col'
        )}
      >
        <div className="flex items-center justify-between mb-6 content-month">
          <h2 className="subtitle text-ins-blue">{actualMonthName}</h2>
          <div className="gap-6 flex justify-between md:justify-normal ml-auto">
            <div className="flex items-center justify-center gap-6 max-w-[65px] w-full">
              <button
                type="button"
                onClick={handlePrev}
                disabled={visibleStartIndex === 0}
                className="bg-transparent border-none cursor-pointer disabled:text-gray-400 text-ins-blue"
              >
                <Icon name="ArrowLeft" width={15} height={15} className="fill-current" />
              </button>
              <button
                type="button"
                onClick={handleNext}
                disabled={visibleStartIndex + 7 >= daysOfWeek.length}
                className="bg-transparent border-none cursor-pointer disabled:text-gray-400 text-ins-blue"
              >
                <Icon name="ArrowRight" width={15} height={15} className="fill-current" />
              </button>
            </div>
            <div className="gap-1.5">
              <div
                className="group flex items-center justify-center gap-2 w-[150px] h-10 py-2.5 px-4 border border-ins-blue rounded-lg text-ins-blue cursor-pointer text-sm hover:bg-ins-blue hover:text-white"
                onClick={(e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)}
              >
                <Icon
                  name="Calendar"
                  width={20}
                  height={20}
                  className="fill-ins-blue group-hover:fill-white"
                />
                Outro dia
              </div>
              <CalendarPopup
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                date={customDate}
                onDateChange={(newDate) => {
                  if (newDate) {
                    setCustomDate(newDate);
                    setDisplayedDate(newDate);
                  } else {
                    setCustomDate(null);
                    setSelectedDay(null);
                    setDisplayedDate(new Date());
                  }
                }}
                anchorOriginVertical={45}
                anchorOriginHorizontal={0}
                datePickerProps={{
                  sx: {
                    '& .MuiPickersDay-root': {
                      borderRadius: '50%',
                    },
                  },
                  minDate: today,
                  shouldDisableDate: (day) => day.getDay() === 0,
                }}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col">
          <div className="flex gap-3 items-center mb-5 md:overflow-hidden overflow-auto scroll-snap-x-mandatory scrollbar-hide">
            {visibleDays.map((dia, index) => {
              const realIndex = visibleStartIndex + index;
              return (
                <DayItem
                  key={realIndex}
                  dia={dia}
                  selected={selectedDay === realIndex}
                  disabled={dia.disabled}
                  onClick={() => {
                    if (!dia.disabled) {
                      setSelectedDay(realIndex);
                      setCustomDate(null);
                    }
                  }}
                />
              );
            })}
          </div>

          {(selectedDay !== null || customDate) && (
            <div className="flex flex-col items-baseline md:flex-row md:items-center mt-5">
              <p className="text-sm md:mr-4 md:mb-0 mr-0 mb-4">Selecione o período:</p>
              <div className="flex justify-between gap-4">
                {periodos.map((periodo, index) => (
                  <button
                    key={index}
                    className={clsx(
                      'h-8 flex items-center justify-center flex-1 gap-2 text-xs mx-[5px] py-2 px-3',
                      'border border-ins-blue rounded-2xl cursor-pointer text-ins-blue fill-ins-blue',
                      'group hover:bg-ins-blue hover:text-white',
                      `${periodSelected === periodo ? 'selected bg-ins-blue text-white fill-white' : ''}`
                    )}
                    onClick={() => handlePeriodSelect(periodo)}
                  >
                    <Icon
                      name={PERIOD_ICONS[periodo]}
                      width={16}
                      height={16}
                      className="group-hover:fill-white"
                    />
                    {periodo}
                  </button>
                ))}
              </div>
            </div>
          )}

          {shouldShowHours && (
            <div className="flex flex-col mt-10 gap-2">
              <p>Selecione o horário:</p>
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-7 mt-3 gap-3">
                {availableHours.length > 0 ? (
                  availableHours.map((hour, i) => (
                    <button
                      onClick={() => setHourSelected(hour)}
                      key={i}
                      className={clsx(
                        'hover:bg-ins-blue hover:text-white',
                        'py-2 px-3 border border-ins-blue rounded-lg cursor-pointer',
                        `${hourSelected === hour ? 'selected bg-ins-blue text-white' : 'bg-transparent text-ins-blue'}`
                      )}
                    >
                      {hour}
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-gray-600 col-span-full">
                    Nenhum horário disponível para esta data.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Videoconference;
